import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { CreditCard, Tag, Info, Receipt, Lightning, MagnifyingGlass } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BUSINESS } from "@/data/site";
import PayPalWalletButtons from "@/components/PayPalWalletButtons";

const SURCHARGE_PCT = 5;

const BOOKING_TIERS = [
  { id: "standard", label: "Standard Booking", price: 79, icon: MagnifyingGlass, tag: "Most jobs", desc: "Under 45 minutes from technician location." },
  { id: "emergency", label: "Emergency / Long-Distance", price: 119, icon: Lightning, tag: "45+ min or urgent", desc: "Emergency response or long-distance." },
];

const initial = { name: "", email: "", phone: "", description: "Deposit", base_amount: "" };

export default function DepositPage() {
  const [params] = useSearchParams();
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Pre-fill from ?tier=standard|emergency&for=<service label>
  useEffect(() => {
    const tier = params.get("tier");
    const forSvc = params.get("for");
    const t = BOOKING_TIERS.find((x) => x.id === tier);
    if (t) {
      setForm((f) => ({
        ...f,
        base_amount: String(t.price),
        description: forSvc
          ? `${t.label} — ${forSvc} (credited to final job cost if approved)`
          : `${t.label} (credited to final job cost if approved)`,
      }));
    }
  }, [params]);

  const pickTier = (t) => {
    setForm((f) => ({
      ...f,
      base_amount: String(t.price),
      description: `${t.label} (credited to final job cost if approved)`,
    }));
    toast.success(`${t.label} — $${t.price} selected.`);
  };

  const base = parseFloat(form.base_amount) || 0;
  const surcharge = Math.round(base * (SURCHARGE_PCT / 100) * 100) / 100;
  const total = Math.round((base + surcharge) * 100) / 100;

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Valid email required";
    if (!form.description.trim()) e.description = "What's this payment for?";
    if (!base || base < 1 || base > 25000) e.base_amount = "Amount must be between $1 and $25,000";
    setErrors(e); return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error("Please fix the highlighted fields."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/square/deposit", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone || null,
        description: form.description.trim(),
        base_amount: base,
        origin_url: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
      else toast.error("Checkout link not returned. Please try again.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Payment could not start");
    } finally { setSubmitting(false); }
  };

  // Called by <PayPalWalletButtons> when the buyer clicks any PayPal wallet button.
  // Must validate + return the PayPal order_id. Throws to abort the flow.
  const createPayPalOrder = async () => {
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      throw new Error("form invalid");
    }
    const { data } = await api.post("/square/deposit", {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone || null,
      description: form.description.trim(),
      base_amount: base,
      origin_url: window.location.origin,
    });
    if (!data?.order_id) throw new Error("No order id");
    return data.order_id;
  };

  return (
    <div data-testid="deposit-page">
      <section className="ji-container pt-16 md:pt-24 pb-10">
        <div className="ji-overline mb-5 inline-flex items-center gap-2">
          <Receipt size={12} weight="fill" /> Deposits & Fees · Powered by PayPal
        </div>
        <h1 style={{ fontFamily: "Cabinet Grotesk", lineHeight: 1.0 }} className="text-5xl md:text-6xl font-bold tracking-tight max-w-3xl">
          Pay a deposit or fee —<br /><span className="text-[var(--ji-accent)] italic">secure card checkout.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[var(--ji-muted)] leading-relaxed">
          Enter the deposit or fee amount you were quoted, and we&rsquo;ll send you to PayPal&rsquo;s secure checkout page (works with PayPal balance, bank, or any major card).
          A <strong className="text-[var(--ji-text)]">5% surcharge is added to all credit card payments</strong> to cover processing fees.
        </p>
        <p className="mt-3 max-w-2xl text-sm text-[var(--ji-muted)]">
          <strong className="text-[var(--ji-text)]">Booking non-repair services?</strong> Pick a tier below — $79 to schedule or $119 for emergency/long-distance (over 45 min from technician). Fee is credited toward your final job cost when work is approved. <em>Hassle-free. No surprise fees.</em>
        </p>
      </section>

      {/* Quick-pick booking tiers */}
      <section className="ji-container pb-6" data-testid="deposit-tier-picker">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {BOOKING_TIERS.map((t) => {
            const Icon = t.icon;
            const active = parseFloat(form.base_amount) === t.price;
            return (
              <button
                type="button"
                key={t.id}
                data-testid={`deposit-tier-${t.id}`}
                onClick={() => pickTier(t)}
                className={`text-left p-5 border-2 transition-all ${
                  active
                    ? "border-[var(--ji-accent)] bg-[var(--ji-surface-alt)]"
                    : "border-[var(--ji-border)] hover:border-[var(--ji-muted)]"
                }`}
                style={{ borderRadius: 4 }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Icon size={20} weight="duotone" className={active ? "text-[var(--ji-accent)]" : "text-[var(--ji-primary)]"} />
                    <span style={{ fontFamily: "Cabinet Grotesk" }} className="text-lg font-bold">{t.label}</span>
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.15em] text-[var(--ji-muted)] whitespace-nowrap">{t.tag}</span>
                </div>
                <div className="mt-3 flex items-baseline gap-1">
                  <span style={{ fontFamily: "Cabinet Grotesk" }} className={`text-4xl font-bold ${active ? "text-[var(--ji-accent)]" : "text-[var(--ji-primary)]"}`}>${t.price}</span>
                  <span className="text-xs text-[var(--ji-muted)]">flat · credited to final job</span>
                </div>
                <p className="mt-2 text-xs text-[var(--ji-muted)] leading-relaxed">{t.desc}</p>
              </button>
            );
          })}
        </div>
      </section>

      <section className="ji-container pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div data-testid="deposit-form" className="ji-card p-7 md:p-10 space-y-5" style={{ borderRadius: 4 }}>
            <div className="ji-overline inline-flex items-center gap-2"><Tag size={12} weight="fill" /> Payment Details</div>
            <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl md:text-3xl font-bold tracking-tight">Enter your details.</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2">
              <div className="md:col-span-2">
                <Label htmlFor="d-amount" className="text-sm font-medium">Base amount (USD) *</Label>
                <div className="relative mt-2">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--ji-muted)] font-semibold">$</span>
                  <Input id="d-amount" type="number" step="0.01" min="1" max="25000" data-testid="deposit-amount"
                    value={form.base_amount} onChange={(e) => update("base_amount", e.target.value)}
                    className="h-14 pl-8 text-2xl font-bold" placeholder="0.00" />
                </div>
                {errors.base_amount && <p className="text-xs text-red-600 mt-1">{errors.base_amount}</p>}
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="d-desc" className="text-sm font-medium">What&rsquo;s this payment for? *</Label>
                <Input id="d-desc" data-testid="deposit-description" value={form.description} onChange={(e) => update("description", e.target.value)} className="h-12 mt-2" placeholder="e.g. Deposit on fridge repair · Cleanup deposit · Courier retainer" />
                {errors.description && <p className="text-xs text-red-600 mt-1">{errors.description}</p>}
              </div>
              <div>
                <Label htmlFor="d-name" className="text-sm font-medium">Full name *</Label>
                <Input id="d-name" data-testid="deposit-name" value={form.name} onChange={(e) => update("name", e.target.value)} className="h-12 mt-2" placeholder="Jane Doe" />
                {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
              </div>
              <div>
                <Label htmlFor="d-phone" className="text-sm font-medium">Phone</Label>
                <Input id="d-phone" data-testid="deposit-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="h-12 mt-2" placeholder="(504) 555-0100" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="d-email" className="text-sm font-medium">Email *</Label>
                <Input id="d-email" type="email" data-testid="deposit-email" value={form.email} onChange={(e) => update("email", e.target.value)} className="h-12 mt-2" placeholder="you@email.com" />
                {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
              </div>
            </div>

            <div className="pt-2">
              <PayPalWalletButtons
                amount={total}
                disabled={submitting || !base}
                createOrder={createPayPalOrder}
                onApproved={(sessionId) => { window.location.href = `/payments/success?session_id=${sessionId}`; }}
              />
              <p className="mt-3 text-xs text-[var(--ji-muted)]">
                Total to charge: <strong className="text-[var(--ji-text)]">${total.toFixed(2)}</strong> · includes 5% card processing surcharge.
              </p>
            </div>
          </div>
        </div>

        <aside className="lg:col-span-5 space-y-5 lg:sticky lg:top-24 self-start">
          <div className="ji-card p-8" style={{ borderRadius: 4 }} data-testid="deposit-summary">
            <div className="ji-overline mb-4 inline-flex items-center gap-2"><CreditCard size={12} weight="fill" /> Order summary</div>
            <div className="space-y-3 text-sm">
              <div className="flex items-baseline justify-between">
                <span className="text-[var(--ji-muted)]">Base amount</span>
                <span style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl font-bold">${base.toFixed(2)}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-[var(--ji-muted)]">Credit card surcharge ({SURCHARGE_PCT}%)</span>
                <span style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl font-bold text-[var(--ji-accent)]">${surcharge.toFixed(2)}</span>
              </div>
              <div className="border-t border-[var(--ji-border)] pt-3 flex items-baseline justify-between">
                <span className="text-[var(--ji-text)] font-semibold">Total to charge</span>
                <span style={{ fontFamily: "Cabinet Grotesk" }} className="text-4xl font-bold text-[var(--ji-primary)]">${total.toFixed(2)}</span>
              </div>
            </div>
            <div className="mt-5 text-xs text-[var(--ji-muted)] inline-flex items-start gap-2">
              <Info size={14} weight="duotone" className="mt-0.5 flex-shrink-0" />
              <span>The 5% surcharge covers card processing fees. Paying by check or cash? Give David a call to arrange — no surcharge.</span>
            </div>
          </div>

          <div className="ji-card p-6" style={{ borderRadius: 4 }}>
            <div className="ji-overline mb-3">Prefer to pay by check?</div>
            <a href={`tel:${BUSINESS.primaryPhone.tel}`} className="ji-btn-outline w-full inline-flex items-center justify-center gap-2 px-6 h-12 text-sm font-semibold" style={{ borderRadius: 4 }}>
              Call {BUSINESS.primaryPhone.display}
            </a>
          </div>
        </aside>
      </section>
    </div>
  );
}
